// Hoeveel haast heeft een doel? De route, de ring om de klant, de pijlen en de kaart tonen dat met kleur.
// In de kleurenblindveilige stand krijgt elk niveau ook een eigen patroon en symbool, zodat kleur nooit het enige signaal is.
// Pure logica (zonder canvas): de tekenfuncties in render/targets.js lezen hier alleen uit.

export type Urgency = 'ok' | 'warn' | 'late' | 'bakery';

export interface UrgencyStyle {
  color: string;
  /** Streepjespatroon van de routelijn ([] is doorgetrokken) */
  routeDash: number[];
  routeWidth: number;
  /** Streepjespatroon van de rand om een doel */
  ringDash: number[];
  /** Symbool bij de naam en op de randpijl ('' is geen symbool) */
  glyph: string;
}

export interface UrgencyInput {
  kind: string;
  late?: boolean;
  /** Resterende tijd als deel van de deadline (1 is net begonnen, 0 is voorbij) */
  fraction: number;
}

export function urgencyOf(target: UrgencyInput): Urgency {
  if (target.kind === 'bakery') return 'bakery';
  if (target.late || target.fraction < 0.2) return 'late';
  if (target.fraction < 0.5) return 'warn';
  return 'ok';
}

/** Zoals het spel altijd was: groen, oranje en rood, met dezelfde gestippelde lijn. */
const STANDARD: Record<Urgency, UrgencyStyle> = {
  ok: { color: '#10b981', routeDash: [14, 12], routeWidth: 5, ringDash: [], glyph: '' },
  warn: { color: '#f59e0b', routeDash: [14, 12], routeWidth: 5, ringDash: [], glyph: '' },
  late: { color: '#ef4444', routeDash: [14, 12], routeWidth: 5, ringDash: [], glyph: '' },
  bakery: { color: '#f59e0b', routeDash: [14, 12], routeWidth: 5, ringDash: [], glyph: '' },
};

/**
 * Kleurenblindveilig: blauw, geel en roze (te onderscheiden bij rood-groenblindheid) én een verschillend patroon.
 * Op tijd is een lange streep, krap is stippen met een driehoekje, te laat is een dikke doorgetrokken lijn met een waarschuwing.
 */
const SAFE: Record<Urgency, UrgencyStyle> = {
  ok: { color: '#56b4e9', routeDash: [20, 12], routeWidth: 5, ringDash: [], glyph: '' },
  warn: { color: '#f0e442', routeDash: [3, 11], routeWidth: 6, ringDash: [12, 9], glyph: '▲' },
  late: { color: '#cc79a7', routeDash: [], routeWidth: 9, ringDash: [4, 6], glyph: '⚠' },
  bakery: { color: '#e69f00', routeDash: [20, 12], routeWidth: 5, ringDash: [], glyph: '' },
};

export const urgencyStyle = (level: Urgency, colorblind: boolean): UrgencyStyle => (colorblind ? SAFE : STANDARD)[level];

/** Kleuren van de balkjes in de lading (tijd en gaafheid) voor de gewone en de kleurenblindveilige stand. */
export function meterColors(colorblind: boolean) {
  return colorblind
    ? { good: '#56b4e9', mid: '#f0e442', bad: '#cc79a7', cold: '#56b4e9', warm: '#e69f00', care: '#56b4e9' }
    : { good: '#10b981', mid: '#f59e0b', bad: '#ef4444', cold: '#38bdf8', warm: '#f97316', care: '#a78bfa' };
}
